import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, UserCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canManage } from "@/contexts/AuthContext";
import { useEmpresa } from "@/contexts/EmpresaContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_empleados_seen";

function tourSteps(user) {
  const steps = [
    { target: "[data-tour='empleados-filtros']", title: "Busca y filtra", content: "Busca por nombre, número, RFC o CURP, y filtra por estatus (activo, baja, suspendido, incapacidad, vacaciones)." },
  ];
  if (canManage(user)) {
    steps.push({ target: "[data-tour='empleados-nuevo']", title: "Alta de empleado", content: "Registra RFC, CURP, puesto, centro de trabajo y horario asignado — esta identificación es obligatoria conforme la LFT." });
  }
  steps.push({ target: "[data-tour='empleados-tabla']", title: "Plantilla", content: "Consulta el listado completo con su centro, horario y estatus actual." });
  return steps;
}

const ESTATUS_OPTS = ["activo", "baja", "suspendido", "incapacidad", "vacaciones"];

const STATUS_COLOR = {
  activo: "bg-emerald-100 text-emerald-800",
  baja: "bg-red-100 text-red-800",
  suspendido: "bg-amber-100 text-amber-800",
  incapacidad: "bg-blue-100 text-blue-800",
  vacaciones: "bg-violet-100 text-violet-800",
};

export default function Empleados() {
  const { user } = useAuth();
  const { esEscuela } = useEmpresa();
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [search, setSearch] = useState("");
  const [estatusF, setEstatusF] = useState("todos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (estatusF !== "todos") params.set("estatus", estatusF);
    const [emp, e, c, h] = await Promise.all([
      api.get("/empleados?" + params.toString()),
      api.get("/empresas"),
      api.get("/centros"),
      api.get("/horarios"),
    ]);
    setItems(emp.data);
    setEmpresas(e.data);
    setCentros(c.data);
    setHorarios(h.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estatusF]);

  const centroName = (id) => centros.find((c) => c.id === id)?.nombre || "—";
  const horarioName = (id) => horarios.find((h) => h.id === id)?.nombre || "—";

  const startCreate = () => {
    setEditing(null);
    setForm({
      empresa_id: empresas[0]?.id || "", centro_id: centros[0]?.id || "",
      numero_empleado: "", nombre: "", rfc: "", curp: "", puesto: "",
      departamento: "", area: "", tipo_contrato: "Indeterminado",
      fecha_ingreso: hoyISO(),
      estatus: "activo", horario_id: horarios[0]?.id || "", modalidad: "presencial",
      biometric_id: "", email: "", telefono: "",
      ...(esEscuela ? { tipo_personal: "jornada", tarifa_hora_clase: null } : {}),
    });
    setOpen(true);
  };
  const startEdit = (e) => { setEditing(e); setForm(e); setOpen(true); };

  // Borrado lógico: para corregir un alta equivocada. El registro se conserva en BD
  // pero desaparece de la plantilla y libera su número/RFC para volver a capturarlo.
  const remove = async (e) => {
    if (!window.confirm(`Eliminar a "${e.nombre}" de la plantilla. Úsalo sólo para corregir un alta equivocada; el registro deja de aparecer en las listas. ¿Continuar?`)) return;
    try {
      await api.delete(`/empleados/${e.id}`);
      toast.success("Empleado eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      // La empresa es la del centro elegido (el backend también la deriva así).
      const centro = centros.find((c) => c.id === form.centro_id);
      const data = { ...form, empresa_id: centro?.empresa_id || form.empresa_id };
      if (editing) await api.put(`/empleados/${editing.id}`, data);
      else await api.post("/empleados", data);
      toast.success(editing ? "Empleado actualizado" : "Empleado creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="empleados-page">
      <PageHeader
        kicker="Plantilla"
        title="Empleados"
        subtitle="Identificación inequívoca, RFC, CURP, puesto y horario asignado por empleado."
        actions={
          <div className="flex gap-2 items-center">
            {canManage(user) && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="emp-new-btn" data-tour="empleados-nuevo">
                <Plus size={16} weight="bold" className="mr-2" /> Nuevo empleado
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="flex gap-3 mb-4 flex-wrap" data-tour="empleados-filtros">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, número, RFC, CURP…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9"
            data-testid="emp-search"
          />
        </div>
        <Select value={estatusF} onValueChange={setEstatusF}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estatus</SelectItem>
            {ESTATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}>Filtrar</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="empleados-tabla">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Empleado</th>
              <th>RFC / CURP</th>
              <th>Puesto</th>
              <th>Centro</th>
              <th>Horario</th>
              <th>Estatus</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-8">Sin empleados</td></tr>}
            {items.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-700">
                      <UserCircle size={26} weight="duotone" />
                    </div>
                    <div>
                      <div className="font-semibold text-ink">{e.nombre}</div>
                      <div className="text-xs text-slate-500 font-mono">{e.numero_empleado} · BIO: {e.biometric_id || "—"}</div>
                      {esEscuela && e.tipo_personal === "docente" && (
                        <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-accent-soft text-brand-accent">Docente</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="text-xs font-mono">{e.rfc || "—"}<br/><span className="text-slate-500">{e.curp || ""}</span></td>
                <td><div>{e.puesto}</div><div className="text-xs text-slate-500">{e.departamento}</div></td>
                <td>{centroName(e.centro_id)}</td>
                <td className="text-xs">{horarioName(e.horario_id)}</td>
                <td><span className={`text-xs px-2 py-1 rounded ${STATUS_COLOR[e.estatus] || "bg-slate-100"}`}>{e.estatus}</span></td>
                <td>
                  {canManage(user) && <Button variant="ghost" size="sm" onClick={() => startEdit(e)} data-testid={`emp-edit-${e.id}`}><PencilSimple size={16} /></Button>}
                  {canManage(user) && <Button variant="ghost" size="sm" onClick={() => remove(e)} data-testid={`emp-del-${e.id}`} title="Eliminar (corregir alta)"><Trash size={16} className="text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1"><Label className="label-uppercase">N° empleado</Label><Input value={form.numero_empleado || ""} onChange={(e) => setForm({ ...form, numero_empleado: e.target.value })} data-testid="emp-num-input" /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="label-uppercase">Nombre completo</Label><Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="emp-nombre-input" /></div>
            <div className="space-y-1"><Label className="label-uppercase">RFC</Label><Input value={form.rfc || ""} onChange={(e) => setForm({ ...form, rfc: e.target.value })} /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="label-uppercase">CURP</Label><Input value={form.curp || ""} onChange={(e) => setForm({ ...form, curp: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Puesto</Label><Input value={form.puesto || ""} onChange={(e) => setForm({ ...form, puesto: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Departamento</Label><Input value={form.departamento || ""} onChange={(e) => setForm({ ...form, departamento: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Tipo contrato</Label><Input value={form.tipo_contrato || ""} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })} /></div>
            <div className="space-y-1">
              <Label className="label-uppercase">Centro</Label>
              <Select value={form.centro_id || ""} onValueChange={(v) => setForm({ ...form, centro_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Horario</Label>
              <Select value={form.horario_id || ""} onValueChange={(v) => setForm({ ...form, horario_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{horarios.map((h) => <SelectItem key={h.id} value={h.id}>{h.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Modalidad</Label>
              <Select value={form.modalidad || "presencial"} onValueChange={(v) => setForm({ ...form, modalidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="teletrabajo">Teletrabajo</SelectItem>
                  <SelectItem value="hibrida">Híbrida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Estatus</Label>
              <Select value={form.estatus || "activo"} onValueChange={(v) => setForm({ ...form, estatus: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ESTATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="label-uppercase">Fecha ingreso</Label><Input type="date" value={form.fecha_ingreso || ""} onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">ID biométrico</Label><Input value={form.biometric_id || ""} onChange={(e) => setForm({ ...form, biometric_id: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Teléfono</Label><Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            {esEscuela && (
              <div className="space-y-1">
                <Label className="label-uppercase">Tipo de personal</Label>
                <Select value={form.tipo_personal || "jornada"} onValueChange={(v) => setForm({ ...form, tipo_personal: v })}>
                  <SelectTrigger data-testid="emp-tipo-personal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jornada">Jornada (administrativo)</SelectItem>
                    <SelectItem value="docente">Docente (por hora-clase)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {esEscuela && form.tipo_personal === "docente" && (
              <div className="space-y-1">
                <Label className="label-uppercase">Tarifa hora-clase</Label>
                <Input
                  type="number" min={0} step="0.01" placeholder="La de la escuela"
                  value={form.tarifa_hora_clase ?? ""}
                  onChange={(e) => setForm({ ...form, tarifa_hora_clase: e.target.value === "" ? null : Number(e.target.value) })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="emp-save-btn">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
